import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Home, LogOut, Send, CheckCircle, Clock, Calendar, MessageSquare, AlertTriangle, ScanLine } from 'lucide-react';
import API_URL from '../config';
import './Home.css';
import './faculty/FacultyHome.css';

const Attendance = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [activeTab, setActiveTab] = useState("request");

    // Form state
    const [date, setDate] = useState("");
    const [type, setType] = useState("full_day");
    const [selectedPeriods, setSelectedPeriods] = useState([]);
    const [reason, setReason] = useState("");
    const [attPercent, setAttPercent] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // History state
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    // Class Attendance (scanner-based) state
    const [classAttendance, setClassAttendance] = useState([]);
    const [loadingClassAtt, setLoadingClassAtt] = useState(false);

    const periods = [1, 2, 3, 4, 5, 6, 7];

    useEffect(() => {
        // Strict same-day requirement
        const today = new Date().toISOString().split('T')[0];
        setDate(today);
    }, []);

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
        if (!userData.email || (userData.role !== 'student' && userData.role !== 'admin')) {
            navigate("/");
        } else {
            setUser(userData);
        }
    }, [navigate]);

    useEffect(() => {
        if (activeTab === "history" && user?.email) {
            fetchHistory();
        }
        if (activeTab === "class_attendance" && user?.rollNo) {
            fetchClassAttendance();
        }
    }, [activeTab, user]);

    const fetchHistory = async () => {
        setLoadingHistory(true);
        try {
            const res = await fetch(`${API_URL}/attendance/student/${user.email}`);
            const data = await res.json();
            setHistory(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Failed to fetch attendance history", error);
        } finally {
            setLoadingHistory(false);
        }
    };

    const fetchClassAttendance = async () => {
        setLoadingClassAtt(true);
        try {
            const res = await fetch(`${API_URL}/scanner-attendance/student/${user.rollNo}`);
            const data = await res.json();
            setClassAttendance(data.ok ? (data.records || []) : []);
        } catch (err) {
            console.error('Failed to fetch class attendance', err);
        } finally {
            setLoadingClassAtt(false);
        }
    };

    const togglePeriod = (p) => {
        setSelectedPeriods(prev =>
            prev.includes(p) ? prev.filter(item => item !== p) : [...prev, p]
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // TEMPORARY PAUSE FOR PRODUCTION
        const allowedAdmins = ['24071a6691@vnrvjiet.in', '24071a6679@vnrvjiet.in'];
        if (user && !allowedAdmins.includes(user.email)) {
            alert("We are testing this module.\n\nIt'll be live soon.");
            return;
        }

        if (!date || !reason || (type === "periods" && selectedPeriods.length === 0)) {
            alert("Please fill in all required fields.");
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                student_email: user.email,
                date,
                type,
                periods: type === 'periods' ? selectedPeriods : null,
                reason,
                attendance_percentage: attPercent
            };

            const res = await fetch(`${API_URL}/attendance/request`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                alert("Attendance request submitted successfully!");
                setActiveTab("history");
                // Reset form
                setDate(new Date().toISOString().split('T')[0]);
                setType("full_day");
                setSelectedPeriods([]);
                setReason("");
                setAttPercent("");
            } else {
                alert("Failed to submit request.");
            }
        } catch (error) {
            console.error(error);
            alert("An error occurred while submitting.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this pending request?")) return;
        try {
            const res = await fetch(`${API_URL}/attendance/request/${id}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ student_email: user.email })
            });
            if (res.ok) {
                alert("Request deleted successfully");
                fetchHistory();
            } else {
                const err = await res.json();
                alert(err.error || "Failed to delete");
            }
        } catch (error) { console.error(error); }
    };

    const getStatusColor = (status) => {
        if (status === 'rejected') return '#ef4444'; // red
        if (status === 'attendance_given') return '#10b981'; // green
        return '#f59e0b'; // pending warning orange
    };

    const formatStatus = (status) => {
        const map = {
            'pending_coordinator': 'Pending Class Coordinator',
            'pending_hod': 'Pending HOD',
            'pending_faculty': 'Pending Respective Faculty',
            'attendance_given': 'Attendance Given 🟢',
            'rejected': 'Rejected 🔴'
        };
        return map[status] || status;
    };

    return (
        <div className="home-page-container">
            <div className="home-content-wrapper" style={{ maxWidth: '900px', margin: '0 auto' }}>
                <header style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button onClick={() => navigate('/home')} className="back-btn-modern" style={{ position: 'static', transform: 'none', flexShrink: 0 }}>
                        <ChevronLeft size={20} /> Back
                    </button>
                    <div style={{ flex: 1, minWidth: '120px' }}>
                        <h1 className="brand-title" style={{ fontSize: '2rem', margin: 0 }}>Attendance Permission</h1>
                        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Request permission and track your approval status.</p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                        <button onClick={() => navigate('/home')} title="Home" style={{ width: 46, height: 46, borderRadius: '14px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexShrink: 0 }}><Home size={20} /></button>
                        <button onClick={() => { localStorage.removeItem('vjconnect_user'); navigate('/'); }} title="Logout" style={{ width: 46, height: 46, borderRadius: '14px', border: 'none', background: '#ef4444', color: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(239,68,68,0.4)', flexShrink: 0 }}><LogOut size={20} /></button>
                    </div>
                </header>



                {/* Tabs */}
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
                    {[{ id: 'request', label: 'Submit Request' }, { id: 'history', label: 'All Previous Approvals' }, { id: 'class_attendance', label: '📷 Class Attendance' }].map(t => (
                        <button key={t.id} onClick={() => setActiveTab(t.id)}
                            style={{ padding: '0.8rem 2rem', borderRadius: '30px', border: '1px solid var(--glass-border)', background: activeTab === t.id ? (t.id === 'class_attendance' ? 'linear-gradient(135deg, #10b981, #059669)' : 'var(--accent-color)') : 'var(--glass-bg)', color: activeTab === t.id ? 'white' : 'var(--text-main)', cursor: 'pointer', fontWeight: 'bold', transition: 'all 0.2s' }}
                        >{t.label}</button>
                    ))}
                </div>

                {/* REQUEST FORM */}
                {activeTab === "request" && (
                    <div className="glass-card" style={{ padding: '2rem' }}>
                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            <div className="profile-field">
                                <label style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Date of Absence (Only Present Day) *</label>
                                <input
                                    type="date"
                                    name="date"
                                    value={date}
                                    readOnly
                                    className="search-input-modern"
                                    style={{ width: '100%', padding: '0.8rem', marginTop: '0.5rem', background: 'rgba(255,255,255,0.05)', cursor: 'not-allowed' }}
                                    required
                                />
                            </div>

                            <div className="profile-field">
                                <label style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Current Attendance % *</label>
                                <input
                                    type="number"
                                    min="0" max="100"
                                    value={attPercent}
                                    onChange={e => setAttPercent(e.target.value)}
                                    placeholder="e.g. 85"
                                    className="search-input-modern"
                                    style={{ width: '100%', padding: '0.8rem', marginTop: '0.5rem' }}
                                    required
                                />
                                <div style={{
                                    marginTop: '0.8rem',
                                    padding: '0.8rem',
                                    border: '1px solid #ef4444',
                                    borderRadius: '8px',
                                    color: '#ef4444',
                                    fontSize: '0.85rem',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    animation: 'blinker 1.5s linear infinite'
                                }}>
                                    ⚠️ Enter the correct attendance as it matches in the HOD level with eduprime ,if proved wrong whole permissions will automatically be rejected from next time !!!
                                    <style>{`
                                        @keyframes blinker {
                                            50% { opacity: 0; }
                                        }
                                    `}</style>
                                </div>
                            </div>

                            <div className="profile-field">
                                <label style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Leave Type *</label>
                                <select
                                    value={type}
                                    onChange={e => setType(e.target.value)}
                                    className="modern-select"
                                    style={{ width: '100%', padding: '0.8rem', marginTop: '0.5rem' }}
                                >
                                    <option value="full_day">Full Day</option>
                                    <option value="periods">Specific Periods</option>
                                </select>
                            </div>

                            {type === "periods" && (
                                <div className="profile-field">
                                    <label style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Select Periods Missed *</label>
                                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                                        {periods.map(p => (
                                            <button
                                                key={p} type="button"
                                                onClick={() => togglePeriod(p)}
                                                style={{
                                                    padding: '0.6rem 1rem', borderRadius: '8px',
                                                    background: selectedPeriods.includes(p) ? 'var(--accent-color)' : 'var(--glass-bg)',
                                                    border: '1px solid var(--glass-border)', color: selectedPeriods.includes(p) ? 'white' : 'var(--text-main)', cursor: 'pointer', fontWeight: selectedPeriods.includes(p) ? 'bold' : 'normal'
                                                }}
                                            >
                                                Period {p}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="profile-field">
                                <label style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Reason for Absence *</label>
                                <textarea
                                    value={reason}
                                    onChange={e => setReason(e.target.value)}
                                    placeholder="Briefly describe why you missed these periods..."
                                    className="search-input-modern"
                                    style={{ width: '100%', padding: '1rem', marginTop: '0.5rem', minHeight: '100px', resize: 'vertical' }}
                                    required
                                />
                            </div>

                            <div style={{ height: '1px', background: 'var(--glass-border)', margin: '1rem 0' }} />

                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'var(--accent-color)', color: 'white', border: 'none', padding: '0.8rem 2rem', borderRadius: '30px', cursor: isSubmitting ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '1rem' }}
                                >
                                    <Send size={20} /> {isSubmitting ? 'Submitting...' : 'Submit Request'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* HISTORY/STATUS */}
                {activeTab === "history" && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {loadingHistory ? (
                            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>Loading...</div>
                        ) : history.length === 0 ? (
                            <div className="glass-card" style={{ textAlign: 'center', padding: '3rem' }}>
                                <AlertTriangle size={40} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                                <p style={{ color: 'var(--text-secondary)' }}>You haven't requested attendance yet.</p>
                            </div>
                        ) : history.map(req => (
                            <div key={req.id} className="glass-card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem', borderLeft: `4px solid ${getStatusColor(req.status)}` }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ fontWeight: 'bold', fontSize: '1.2rem', color: 'var(--text-primary)' }}>
                                        {formatStatus(req.status)}
                                    </div>
                                    <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                                        Requested on: {new Date(req.created_at).toLocaleDateString()}
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)' }}>
                                        <Calendar size={18} color="var(--accent-color)" /> Date of Leave: {req.date}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)' }}>
                                        <Clock size={18} color="var(--accent-color)" /> Periods: {req.type === 'full_day' ? 'Full Day' : (req.periods ? req.periods.join(', ') : 'N/A')}
                                    </div>
                                </div>
                                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '1rem', borderRadius: '8px', fontSize: '0.9rem', color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                                    <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'flex-start' }}>
                                        <MessageSquare size={16} /> <span>{req.reason}</span>
                                    </div>
                                    {req.attendance_percentage !== null && (
                                        <div style={{ fontSize: '0.85rem', color: 'var(--accent-color)', fontWeight: 'bold' }}>
                                            Reported Percentage: {req.attendance_percentage}%
                                        </div>
                                    )}
                                </div>
                                {req.status === 'pending_coordinator' && (
                                    <button
                                        onClick={() => handleDelete(req.id)}
                                        style={{ alignSelf: 'flex-end', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold' }}
                                    >
                                        Delete Request
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                {/* CLASS ATTENDANCE (Scanner-based) */}
                {activeTab === "class_attendance" && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div style={{ padding: '0.8rem 1rem', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '10px', fontSize: '0.85rem', color: '#10b981' }}>
                            📷 Scanner-based attendance from your classes. Updated in real-time when faculty scans your ID.
                        </div>
                        {loadingClassAtt ? (
                            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>Loading...</div>
                        ) : classAttendance.length === 0 ? (
                            <div className="glass-card" style={{ textAlign: 'center', padding: '3rem' }}>
                                <ScanLine size={40} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                                <p style={{ color: 'var(--text-secondary)' }}>No scanner-based attendance records yet.</p>
                            </div>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 0.5rem' }}>
                                    <thead>
                                        <tr>
                                            {['Date', 'Class', 'Session', 'Status'].map(h => (
                                                <th key={h} style={{ textAlign: 'left', padding: '0.6rem 1rem', fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {classAttendance.map((rec, idx) => (
                                            <tr key={rec.id || idx} style={{ background: 'var(--glass-bg)', borderRadius: '10px' }}>
                                                <td style={{ padding: '0.8rem 1rem', borderRadius: '10px 0 0 10px', fontWeight: 600 }}>{rec.date}</td>
                                                <td style={{ padding: '0.8rem 1rem', fontWeight: 500 }}>{rec.class_name}</td>
                                                <td style={{ padding: '0.8rem 1rem', color: 'var(--text-secondary)' }}>{rec.session_type}</td>
                                                <td style={{ padding: '0.8rem 1rem', borderRadius: '0 10px 10px 0' }}>
                                                    <span style={{ background: rec.status === 'present' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', color: rec.status === 'present' ? '#10b981' : '#ef4444', padding: '0.3rem 0.8rem', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 700 }}>
                                                        {rec.status === 'present' ? '✅ Present' : '❌ Absent'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Attendance;
